<template>
  <div
    class="product-personalisation-form personalisation-form"
    :class="{ [$class('loading')]: loading }"
  >
    <div class="personalisation-form__personalise-container form-group">
      <toggle-switch
        id="personalisationActive"
        v-model="personalising"
        v-show="personalisedWithoutInput"
        :label-true="toggleLabels?.personalise"
        :loading="loading"
      />

      <div
        v-show="personalising"
        class="personalisation-form__personalise"
      >
        <p
          v-if="product.personalisation.introMessage"
          class="personalisation-form__intro-message text-body-s-regular mb-m"
          v-text="product.personalisation.introMessage"
        />

        <div
          v-if="hasAllExclusiveLocations"
          class="personalisation-form__exclusive-locations"
        >
          <p
            class="
              personalisation-form__exclusive-label
              text-body-s-bold
              text-body-s-regular-desktop
            "
            v-text="$string('product.personalisation.choose_on_front')"
          />

          <div class="personalisation-form__location-radios">
            <radio-input
              v-for="location of exclusiveLocations"
              :id="`radio-${location.id}`"
              :key="`radio-${location.id}`"
              v-model="currentExclusiveLocationName"
              :label="location.name"
              label-class="text-body-xs-regular text-body-s-regular-desktop"
              :value="location.sanitizedName"
              name="exclusiveLocation"
            />
          </div>

          <div
            v-for="location of exclusiveLocations"
            v-show="location.sanitizedName === currentExclusiveLocationName"
            :key="location.id"
            class="
              personalisation-form__location
              personalisation-form__location--exclusive
            "
            :class="{'personalisation-form__location-spacing': personalisedWithoutInput}"
          >
            <label
              class="visually-hidden"
              :for="location.id"
              v-text="location.name"
            />

            <div
              v-if="location.image"
              class="personalisation-form__location-image-wrapper"
            >
              <responsive-image
                :image="location.image"
                ratio="1:1"
                sizes="(min-width: 1024px) 80px, 56px"
                widths="56, 80, 112, 168, 240"
              />
            </div>

            <div
              v-show="personalisedWithoutInput"
              class="personalisation-form__location-input-wrapper"
            >
              <input
                :id="location.id"
                v-model="localMessageState[location.name]"
                :class="{
                  [$class('error')]: (
                    isProfane.includes(location.name) ||
                    hasEmoji.includes(location.name)
                  )
                }"
                type="text"
                :disabled="!personalising"
                :maxlength="location.characterLimit"
                :placeholder="$string('product.personalisation.input_placeholder')"
              >

              <div
                v-if="isProfane.includes(location.name) || hasEmoji.includes(location.name)"
                class="personalisation-form__input-errors"
              >
                <p
                  v-if="isProfane.includes(location.name)"
                  class="text-body-xs-regular"
                  v-text="$string('product.personalisation.profanity_error')"
                />
                <p
                  v-if="hasEmoji.includes(location.name)"
                  class="text-body-xs-regular"
                  v-text="$string('product.personalisation.emoji_error')"
                />
              </div>

              <p
                v-else
                class="personalisation-form__limit-message text-body-xs-regular"
                :class="{
                  [$class('error')]: showLimitMessageWarning(location)
                }"
                v-html="getLimitMessage(location)"
              />
            </div>
          </div>
        </div>

        <div
          v-for="location of standardLocations"
          :key="location.id"
          class="
            personalisation-form__location
            personalisation-form__location--standard
          "
        >
          <label
            class="
              personalisation-form__location-label
              text-body-s-bold text-body-s-regular-desktop
            "
            :for="location.id"
            v-text="location.name"
          />

          <div
            v-if="location.image"
            class="personalisation-form__location-image-wrapper"
          >
            <responsive-image
              :image="location.image"
              ratio="1:1"
              sizes="(min-width: 1024px) 80px, 56px"
              widths="56, 80, 112, 168, 240"
            />
          </div>

          <div class="personalisation-form__location-input-wrapper">
            <input
              :id="location.id"
              v-model="localMessageState[location.name]"
              :class="{
                [$class('error')]: (
                  isProfane.includes(location.name) ||
                  hasEmoji.includes(location.name)
                )
              }"
              type="text"
              :disabled="!personalising"
              :maxlength="location.characterLimit"
              :placeholder="$string('product.personalisation.input_placeholder')"
            >

            <div
              v-if="isProfane.includes(location.name) || hasEmoji.includes(location.name)"
              class="personalisation-form__input-errors"
            >
              <p
                v-if="isProfane.includes(location.name)"
                class="text-body-xs-regular"
                v-text="$string('product.personalisation.profanity_error')"
              />
              <p
                v-if="hasEmoji.includes(location.name)"
                class="text-body-xs-regular"
                v-text="$string('product.personalisation.emoji_error')"
              />
            </div>

            <p
              v-else
              class="personalisation-form__limit-message text-body-xs-regular"
              :class="{
                [$class('error')]: showLimitMessageWarning(location)
              }"
              v-html="getLimitMessage(location)"
            />
          </div>
        </div>

        <p
          v-show="personalisedWithoutInput"
          class="personalisation-form__profanity-message text-body-xs-bold"
          v-text="$string('product.personalisation.profanity-message')"
        />
      </div>
    </div>

    <divider-line
      v-show="personalisedWithoutInput"
      :label="$string('product.personalisation.or')"
    />

    <div class="personalisation-form__no-text-container">
      <toggle-switch
        id="personalisationInactive"
        :model-value="!personalising"
        v-show="personalisedWithoutInput"
        :label-true="toggleLabels?.noText"
        :loading="loading"
        @update:model-value="() => personalising = !personalising"
      />

      <div class="personalisation-form__no-text-message">
        <p
          v-show="personalisedWithoutInput"
          class="text-body-xs-regular text-body-s-regular-desktop"
          v-text="$string('product.personalisation.no_text_message')"
        />
      </div>
    </div>

    <div
      v-if="$variable('personalisation.disclaimer1') || $variable('personalisation.disclaimer2')"
      class="personalisation-form__disclaimer-container"
    >
      <p
        v-if="$variable('personalisation.disclaimer1')"
        class="personalisation-form__disclaimer"
      >
        <icon-warning />
        <span
          class="text-body-xs-regular"
          v-html="$variable('personalisation.disclaimer1')"
        />
      </p>

      <p
        v-if="$variable('personalisation.disclaimer2')"
        class="personalisation-form__disclaimer"
      >
        <icon-warning />
        <span
          class="text-body-xs-regular"
          v-html="$variable('personalisation.disclaimer2')"
        />
      </p>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product personalisation (product-personalisation-form)
 * -----------------------------------------------------------------------------
 * Product Form for handling personalised products.
 * - Component settings: snippet / dynamic / async / scroll
 * @param {Boolean} [loading] - Loading state.
 * @param {Array} [personalisationGuides] - Data for personalisation guide
 * images.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {String} sectionId - Section ID.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 */

import DividerLine from '~async/utils/divider-line/divider-line'
import RadioInput from '~async/utils/radio-input/radio-input'
import ResponsiveImage from '~global/images/responsive-image'
import ToggleSwitch from '~async/utils/toggle-switch/toggle-switch'
import IconWarning from '~icons/general/warning.svg'

import { checkStringForEmoji, checkStringForProfanity } from '~/helpers/personalisation'

export default {
  name: 'ProductPersonalisationForm',

  components: {
    DividerLine,
    IconWarning,
    RadioInput,
    ResponsiveImage,
    ToggleSwitch,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    personalisationGuides: {
      type: Array,
      default: () => [],
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    sectionId: {
      type: String,
      default: '',
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-personalisation': (payload) => typeof payload === 'object',
    'update-personalisation-error': (payload) => typeof payload === 'boolean',
  },

  data() {

    /**
     * Sets the order that the fields are displayed in.
     */
    const locationsOrder = [
      'front',
      'back',
      'right handle',
      'left handle',
      'top edge',
      'centre',
      'bottom edge',
      'base',
    ]

    /**
     * Groups of exclusive locations.
     * - With these arrays, only one of the locations can be selected - e.g. can
     *   only select 'Front' or 'Back' but not both.
     * - The location that is listed first is the default selected on page load.
     */
    const exclusiveLocationNames = ['front', 'back']
    const exclusiveLocationNamesLeftRight = ['right handle', 'left handle']

    return {
      localMessageState: {},
      personalising: true,
      isProfane: [],
      hasEmoji: [],
      noScriptAddon: this.$variable('personalisation.noScriptAddonProduct'),
      locationsOrder,
      exclusiveLocationNames,
      exclusiveLocationNamesLeftRight,
      currentExclusiveLocationName: exclusiveLocationNames[0],
      variantTitle: '',
      hasExecutedInitialCondition: false,
    }
  },

  computed: {

    /**
     * Compute personalisation line item properties.
     * - When a property is set to `null` the product form component will remove
     *   it from it's lineItems object.
     * @returns {Object}
     */
    lineItemProperties() {
      const properties = {
        _personalised: true,
        'No Message': this.personalising ? null : 'No Message',
        _personalisationAddon: this.personalising ? null : this.noScriptAddon?.id,
        _groupId: this.personalising ? null : Date.now(),
      }

      // Set Right Handle or Left Handle property based on radio selection
      if (this.personalisedWithoutInput) {
        Object.entries(this.localMessageState).forEach(([key, value]) => {
          properties[key] = value && this.personalising ? value : null
        })
      } else if ((this.currentExclusiveLocationName === this.exclusiveLocationNamesLeftRight[0]) ||
        (this.currentExclusiveLocationName === this.exclusiveLocationNames[0])) {
        properties['Right Handle'] = this.$string('product.personalisation.selected')
        properties['Left Handle'] = null
      } else {
        properties['Left Handle'] = this.$string('product.personalisation.selected')
        properties['Right Handle'] = null
      }

      return properties
    },

    /**
     * Checks if the product is personalised but does not require text input.
     * Returns true if personalisation is enabled without input, else false.
    */
    personalisedWithoutInput() {
      return !this.product.personalisation?.enabledWithoutInput
    },

    /**
     * Compute a local version of the locations data.
     * - Injects additional information into the object used for the component.
     * @returns {Array} formatted locations
     */
    locations() {
      return this.product.personalisation?.locations
        .map((location) => {
          const image = this.getLocationImage(location)

          const name = this.getPersonalisationLocationName(location.name)

          return {
            ...location,
            image,
            name,
            id: `${this.sectionId}-${this.product.handle}-location-${name}`,
            sanitizedName: name.toLowerCase(),
          }
        })
        .sort((locationA, locationB) => {
          const locationAIndex = this.locationsOrder.indexOf(locationA.sanitizedName)
          const locationBIndex = this.locationsOrder.indexOf(locationB.sanitizedName)

          return locationAIndex - locationBIndex
        })
    },

    /**
     * Calculate the current personalisation guide based on the product tags.
     * - The personalisation guide name should match the product tag in the
     *   format `PERSONALISAITON GUIDE: {type}`
     * - e.g. `PERSONALISATION_GUIDE: Mugs`
     * @returns {Object} matching personalisation guide.
     */
    personalisationGuide() {
      const personalisationGuide = this.personalisationGuides.find((guide) => {
        return this.product.tags.includes(guide.name)
      })

      return personalisationGuide
    },

    /**
     * Compute price per letter of personalised products.
     * - Subtracts price of double letter variant from single letter variant
     * - Returns string formatted with store currency
     * @returns {String}
     */
    pricePerLetter() {
      const singleLetter = this.product.variants.find(variant => variant.title === '1')
      const doubleLetter = this.product.variants.find(variant => variant.title === '2')

      if (!singleLetter || !doubleLetter) {
        return this.$formatMoney(100, { removeZeros: true })
      }

      const difference = doubleLetter.price.amount - singleLetter.price.amount
      return this.$formatMoney(difference, { removeZeros: true })
    },

    /**
     * Compute an array of location data where the locations are "standard"
     * - Standard locations are placement locations that are not exclusive.
     * @returns {Array}
     */
    standardLocations() {
      if (this.hasAllExclusiveLocations) {
        return this.locations.filter((location) => {
          return !this.activeExclusiveLocationNames.includes(location.sanitizedName)
        })
      }

      return this.locations
    },

    /**
     * Compute if the product has all of the exclusive locations available.
     * - If not all of the exclusive locations are available, they can be
     *   displayed normally.
     * @returns {Boolean}
     */
    hasAllExclusiveLocations() {
      return this.activeExclusiveLocationNames.every((locationName) => {
        return this.locations.some((location) => {
          return location.sanitizedName === locationName
        })
      })
    },

    /**
     * Compute an array of exclusive locations.
     * - Exclusive locations are locations that are mutually exclusive
     * - i.e you cannot personalise multiple of the positions at the same time.
     * - e.g. the user can customise the front body or the back body but not
     *   both.
     * @returns {Array}
     */
    exclusiveLocations() {
      if (!this.hasAllExclusiveLocations) {
        return []
      }

      const exclusiveLocations = this.locations.filter((location) => {
        return this.activeExclusiveLocationNames.includes(location.sanitizedName)
      })

      return exclusiveLocations
    },

    /**
     * Compute the array of exclusive location names to use.
     * - Pairs of exclusive locations are different depending on whether the
     *   `useLeftHandleRightHandleNames` boolean is true
     * @returns {Array} - Exclusive location names.
     */
    activeExclusiveLocationNames() {
      const exclusiveLocations = this.useLeftHandleRightHandleNames
        ? this.exclusiveLocationNamesLeftRight
        : this.exclusiveLocationNames

      return exclusiveLocations
    },

    /**
     * Compute if the personalisation has an error.
     * @returns {Boolean}
     */
    personalisationError() {
      return this.isProfane.length > 0 || this.hasEmoji.length > 0
    },

    /** Compute the variant based on the number of entered characters.
     * @returns {Object|Boolean} - variant object or false.
     */
    personalisationVariant() {
      if (!this.personalising) {
        const variantSubTitle = this.variantTitle.substring(this.variantTitle.indexOf('/'))
        const variant = this.product.variants.find((productVariant) => {
          const variantTitle = productVariant.title
          if (variantTitle.includes('/')) {
            const firstPart = variantTitle.split(' / ')[0]
            const subTitle = variantTitle.substring(variantTitle.indexOf('/'))
            return (Number(firstPart) === 0) && (subTitle === variantSubTitle)
          }
          return Number(variantTitle) === 0
        })

        /**
         * Modify the variant price to include the no scripting add on fee.
         */
        const modifiedVariant = {
          ...variant,
          price: {
            ...variant.price,
            amount: variant.price.amount + this.noScriptAddon.price,
          },
        }

        return modifiedVariant
      }

      if (!this.hasExecutedInitialCondition && this.totalCharacterCount === 0) {
        const value = this.changeInitialCondition()
        return value
      }

      if (this.totalCharacterCount === 0) {
        const variant = this.product.variants.find((productVariant) => {
          const variantTitle = productVariant.title
          const firstPart = variantTitle.split(' / ')[0]
          const productSubTitle = variantTitle.substring(variantTitle.indexOf('/'))
          const variantSubTitle = this.variantTitle.substring(this.variantTitle.indexOf('/'))

          if (firstPart === '0' && (productSubTitle === variantSubTitle)) {
            return true
          }
          return false
        })
        return variant
      }

      const updatedVariantTitle = this.variantTitle.substring(this.variantTitle.indexOf('/'))

      const variant = this.product.variants.find((productVariant) => {
        const variantTitle = productVariant.title
        if (variantTitle.includes('/')) {
          const extractedTitle = variantTitle.split(' / ')[0]
          const subTitle = variantTitle.substring(variantTitle.indexOf('/'))
          const variantTitleNumber = Number(extractedTitle)
          return (
            variantTitleNumber === this.totalCharacterCount &&
            subTitle === updatedVariantTitle
          )
        }
        const variantTitleNumber = Number(variantTitle)
        return variantTitleNumber === this.totalCharacterCount
      })

      return variant || false
    },

    /**
     * Compute labels to pass to the toggle switch components.
     * @returns {Object}
     */
    toggleLabels() {
      const personaliseSubheader = this.$string(
        'product.personalisation.personalise_subheader',
        {
          replace: { amount: this.pricePerLetter },
        },
      )
      const personaliseLabel = `
        <span class="text-other-label-l">
          ${this.$string('product.personalisation.personalise_header')}
          <span class="subheader">
            ${personaliseSubheader}
          </span>
        </span>
      `

      const noTextFee = this.$formatMoney(this.noScriptAddon.price)
      const noTextSubheader = this.$string(
        'product.personalisation.no_text_subheader',
        { replace: { price: noTextFee } },
      )

      const noTextLabel = `
        <span class="text-other-label-l">
          ${this.$string('product.personalisation.no_text_header')}
          <span class="subheader">
            ${noTextSubheader}
          </span>
        </span>
      `

      return {
        personalise: personaliseLabel,
        noText: noTextLabel,
      }
    },

    /**
     * Compute the total character count for personalisation.
     * - Excludes spaces as spaces should not be counted for per letter pricing.
     * @returns {Number} total character count.
     */
    totalCharacterCount() {
      if (!this.personalising) {
        return 0
      }

      return Object.values(this.localMessageState).reduce((total, current) => {
        const messageWithoutSpaces = current.replace(/\s/g, '')
        const length = messageWithoutSpaces.length
        return total + length
      }, 0)
    },

    /**
     * Compute if 'Front body' and 'Back body' should be renamed 'Right Handle'
     * and 'Left Handle'.
     * - Applies to mug products.
     * @returns {Boolean}
     */
    useLeftHandleRightHandleNames() {
      const tagsToCheck = this.$variable('personalisation.leftRightTags')

      const useLeftRight = tagsToCheck.some((tagToCheck) => this.product?.tags.includes(tagToCheck))

      return useLeftRight
    },
  },

  watch: {

    /**
     * Watch local variant change and emit personalisation state to parent
     * component.
     */
    personalisationVariant() {
      this.$emit('update-personalisation', {
        variant: this.personalisationVariant,
        lineItemProperties: this.lineItemProperties,
        personalisation: this.personalising,
        totalCharacterCount: this.totalCharacterCount,
      })
    },

    /**
     * Watch when personalisation toggle is switched and emit state to parent.
     */
    personalising() {
      this.$emit('update-personalisation', {
        variant: this.personalisationVariant,
        lineItemProperties: this.lineItemProperties,
        personalisation: this.personalising,
        totalCharacterCount: this.totalCharacterCount,
      })
    },

    /**
     * Watch if personalisation error state changes and emit up to block add to
     * cart.
     */
    personalisationError() {
      this.$emit('update-personalisation-error', this.personalisationError)
    },

    /**
     * Watch for local message state changes and check each message for
     * profanity.
     */
    localMessageState: {
      handler() {
        const profaneInputs = []
        const emojiInputs = []
        Object.entries(this.localMessageState).forEach(([key, value]) => {
          const isProfane = checkStringForProfanity(value)

          if (isProfane) {
            profaneInputs.push(key)
          }

          const hasEmoji = checkStringForEmoji(value)

          if (hasEmoji) {
            emojiInputs.push(key)
          }
        })

        this.isProfane = profaneInputs
        this.hasEmoji = emojiInputs
      },
      deep: true,
    },

    /**
     * Watch for changes to the activeExclusiveLocationNames array and set the
     * `currentExclusiveLocationName` data to the first item in the updated
     * array.
     */
    activeExclusiveLocationNames: {
      handler() {
        this.currentExclusiveLocationName = this.activeExclusiveLocationNames[0]
      },
      immediate: true,
    },

    /**
     * Watch when the current exclusive location changes.
     * - If there's a current message, moves the current message state from the
     *   old location to the new location.
     * - Clears out any old message state.
     */
    currentExclusiveLocationName(newLocationName, oldLocationName) {
      if (this.personalisedWithoutInput) {
        const localMessageStateKeys = Object.keys(this.localMessageState)

        const oldLocalMessageKey = localMessageStateKeys.find((key) => {
          return key.toLowerCase() === oldLocationName
        })

        const message = this.localMessageState[oldLocalMessageKey] ?? ''

        if (!message) {
          return
        }

        localMessageStateKeys.forEach((key) => {
          const sanitizedKey = key.toLowerCase()

          // Don't clear out the field if its not an exclusive location
          if (!this.activeExclusiveLocationNames.includes(sanitizedKey)) {
            return
          }

          if (sanitizedKey === newLocationName) {
            this.localMessageState[key] = message
            return
          }

          this.localMessageState[key] = ''
        })
      }

      if (this.personalisedWithoutInput) {
        this.$emit('update-personalisation', {
          variant: this.personalisationVariant,
          lineItemProperties: this.lineItemProperties,
        })
      } else {
        this.$emit('update-personalisation', {
          variant: this.variant,
          lineItemProperties: this.lineItemProperties,
        })
      }
    },

    /**
     * Watch variant prop change to reset product options when false.
     */
    variant() {
      if (this.variant || this.variant === null) {
        this.variantTitle = this.variant?.title || ''
        return
      }

      this.setInitialState()
    },
  },

  mounted() {
    this.setInitialState()
  },

  methods: {

    /**
     * Change the initial condition state.
     * - Sets the `hasExecutedInitialCondition` flag to true.
     * - Returns `null` to indicate no specific value is needed.
     */
    changeInitialCondition() {
      this.hasExecutedInitialCondition = true
      return null
    },

    /**
     * Create a message state in data for each location.
     * - Used to handle v-model on the inputs and emitting the data for the
     *   product form.
     */
    setInitialState() {
      this.locations.forEach((location) => {
        this.localMessageState[location.name] = ''
      })
    },

    /**
     * Generate the limit message for the personalisation input.
     * - Displays how many characters are left for each input.
     * @param {Object} location - formatted personalisation location data.
     * @returns {String} - Limit message.
     */
    getLimitMessage(location) {
      const remaining = this.getCharactersRemaining(location)

      return this.$string('product.personalisation.limit_html', {
        replace: { remaining },
        pluralise: remaining,
      })
    },

    /**
     * Calculate remaining characters for a location.
     * @param {Object} location - formatted personalisation location data.
     * @returns {Number} - remaining characters
     */
    getCharactersRemaining(location) {
      const charactersUsed = this.localMessageState[location.name]?.length
      const characterLimit = location.characterLimit
      const remaining = characterLimit - charactersUsed

      return remaining
    },

    /**
     * Get location image from the personalisation guide.
     * @param {Object} location - personalisation location object
     * @returns {Object|Boolean} matched image object or false.
     */
    getLocationImage(location) {
      if (!this.personalisationGuide) {
        return false
      }

      const matchedGuideLocation = this.personalisationGuide.locations.find((guideLocation) => {
        return guideLocation.location === location.name
      })

      return matchedGuideLocation ? matchedGuideLocation.image : false
    },

    /**
     * Handles renaming the location names on the front end.
     * - If the product should use Left Handle / Right Handle names, then we
     *   rename Front Body and Back Body tags to Left Handle andRight Handle on
     *   the front end.
     * - Otherwise, we rename Front Body and Back body to Front and Back
     *   respectively to match the current old theme's logic.
     * - This is done so that Emma Bridgewater can continue to use the existing
     *   tags and metafields they have and the rename is user-facing only.
     * @param {String} name - location name
     * @returns {String} updated location name
     */
    getPersonalisationLocationName(name) {
      switch (name) {
        case 'Front body':
          return this.useLeftHandleRightHandleNames ? 'Right Handle' : 'Front'

        case 'Back body':
          return this.useLeftHandleRightHandleNames ? 'Left Handle' : 'Back'

        default:
          return name
      }
    },

    /**
     * Calculate whether the limit message warning state should show.
     * @returns {Boolean}
     */
    showLimitMessageWarning(location) {
      return this.getCharactersRemaining(location) <=
        this.$variable('personalisation.characterLimitWarning')
    },
  },
}
</script>

<style lang="scss">
@import './product-personalisation-form';
</style>
